interface LoadingSkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: boolean;
}

export function LoadingSkeleton({ 
  className = '', 
  width = 'w-full', 
  height = 'h-4', 
  rounded = false 
}: LoadingSkeletonProps) {
  return (
    <div 
      className={`animate-pulse bg-gray-300 dark:bg-slate-600 ${width} ${height} ${
        rounded ? 'rounded-full' : 'rounded'
      } ${className}`}
    />
  );
}

export function CalculatorSkeleton() {
  return (
    <div className="w-full max-w-md mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 space-y-6">
      {/* Title */}
      <div className="text-center">
        <LoadingSkeleton height="h-8" width="w-3/4" className="mx-auto mb-2" />
        <LoadingSkeleton height="h-4" width="w-full" />
      </div>
      
      {/* Net Income Input */}
      <div className="space-y-2">
        <LoadingSkeleton height="h-4" width="w-1/4" />
        <LoadingSkeleton height="h-12" />
      </div>
      
      {/* Age Category */}
      <div className="space-y-2">
        <LoadingSkeleton height="h-4" width="w-1/3" />
        <div className="grid grid-cols-3 gap-2">
          <LoadingSkeleton height="h-10" rounded />
          <LoadingSkeleton height="h-10" rounded />
          <LoadingSkeleton height="h-10" rounded />
        </div>
      </div>
      
      {/* Advanced Options Toggle */}
      <LoadingSkeleton height="h-6" width="w-1/2" />
      
      {/* Calculate Button */}
      <LoadingSkeleton height="h-12" rounded />
    </div>
  );
}

export function ResultsSkeleton() {
  return (
    <div className="w-full max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 space-y-4">
      {/* Results Title */}
      <LoadingSkeleton height="h-6" width="w-1/3" className="mb-4" />
      
      {/* Main Result */}
      <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg space-y-2">
        <LoadingSkeleton height="h-5" width="w-1/2" />
        <LoadingSkeleton height="h-8" width="w-3/4" />
      </div>
      
      {/* Breakdown Table */}
      <div className="space-y-3">
        <LoadingSkeleton height="h-5" width="w-1/4" />
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
            <LoadingSkeleton height="h-4" width="w-1/3" />
            <LoadingSkeleton height="h-4" width="w-1/4" />
          </div>
        ))}
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-4 pt-4">
        <LoadingSkeleton height="h-10" width="w-24" rounded />
        <LoadingSkeleton height="h-10" width="w-32" rounded />
      </div>
    </div>
  );
}

export function ComparisonSkeleton() {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <LoadingSkeleton height="h-6" width="w-1/4" />
        <LoadingSkeleton height="h-10" width="w-32" rounded />
      </div>
      
      {/* Comparison Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-4 space-y-3">
            <LoadingSkeleton height="h-5" width="w-2/3" />
            <LoadingSkeleton height="h-8" width="w-full" />
            <div className="space-y-2">
              {Array.from({ length: 4 }, (_, j) => (
                <div key={j} className="flex justify-between">
                  <LoadingSkeleton height="h-4" width="w-1/2" />
                  <LoadingSkeleton height="h-4" width="w-1/3" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}